import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Activity, RefreshCw, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Database, Zap, MessageSquare, Smartphone, FileText, Image, Bot,
  Clock, QrCode, ShoppingCart, Mail, Shield
} from "lucide-react";

const STATUS_COLORS = {
  ok: "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  error: "bg-red-100 text-red-700 border-red-200",
  unknown: "bg-gray-100 text-gray-700 border-gray-200",
};

const STATUS_ICONS = {
  ok: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  unknown: Activity,
};

const COMPONENT_ICONS = {
  mongodb: Database,
  redis: Zap,
  whatsapp_service: MessageSquare,
  whatsapp_connections: Smartphone,
  public_menus: FileText,
  media_storage: Image,
  ai_services: Bot,
  schedulers: Clock,
  qr_codes: QrCode,
  recent_orders: ShoppingCart,
  email_service: Mail,
};

function HealthCard({ component }) {
  const StatusIcon = STATUS_ICONS[component.status] || Activity;
  const ComponentIcon = COMPONENT_ICONS[component.key] || Activity;
  const colorClass = STATUS_COLORS[component.status] || STATUS_COLORS.unknown;

  return (
    <Card className={`border-l-4 ${component.status === 'ok' ? 'border-l-emerald-500' : component.status === 'warning' ? 'border-l-amber-500' : 'border-l-red-500'}`}
          data-testid={`health-${component.key}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg ${colorClass}`}>
            <ComponentIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-bold text-sm">{component.name}</p>
              <Badge className={`text-[10px] ${colorClass} border`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {component.status.toUpperCase()}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {component.message}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function SystemHealthDashboard({ adminAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (showToast = false) => {
    if (showToast) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await adminAxios.get("/system-health/dashboard");
      setData(res.data);
      if (showToast) toast.success("Dados atualizados!");
    } catch (err) {
      toast.error("Erro ao carregar dados de saude");
      console.error(err);
    }
    
    setLoading(false);
    setRefreshing(false);
  }, [adminAxios]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchData(), 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const overallColor = data?.overall_status === 'healthy' 
    ? 'from-emerald-500 to-teal-600' 
    : data?.overall_status === 'degraded'
    ? 'from-amber-500 to-orange-600'
    : 'from-red-500 to-rose-600';

  const overallText = data?.overall_status === 'healthy'
    ? 'Sistema Saudavel'
    : data?.overall_status === 'degraded'
    ? 'Sistema Degradado'
    : 'Sistema Critico';

  return (
    <div className="space-y-6" data-testid="system-health-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${overallColor} shadow-lg`}>
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-black text-xl tracking-tight">Saude do Sistema</h2>
            <p className="text-xs text-muted-foreground">
              Monitoramento em tempo real - Atualiza a cada 60s
            </p>
          </div>
        </div>
        <Button 
          size="sm" 
          variant="outline" 
          className="gap-1.5 h-8" 
          onClick={() => fetchData(true)}
          disabled={refreshing}
          data-testid="refresh-health"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Overall Status */}
      <Card className={`bg-gradient-to-r ${overallColor} text-white border-0`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium">Status Geral</p>
              <h3 className="text-3xl font-black mt-1">{overallText}</h3>
              <p className="text-white/70 text-xs mt-2">
                Ultima verificacao: {data?.last_check ? new Date(data.last_check).toLocaleString('pt-BR') : '-'}
              </p>
            </div>
            <div className="text-right">
              <div className="flex gap-4">
                <div className="text-center">
                  <p className="text-4xl font-black">{data?.summary?.ok || 0}</p>
                  <p className="text-xs text-white/70">OK</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-black">{data?.summary?.warnings || 0}</p>
                  <p className="text-xs text-white/70">Avisos</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-black">{data?.summary?.errors || 0}</p>
                  <p className="text-xs text-white/70">Erros</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Components Grid */}
      <div>
        <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide mb-3">
          Componentes do Sistema
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data?.components?.map((component, i) => (
            <HealthCard key={i} component={component} />
          ))}
        </div>
      </div>

      {/* Critical Issues Alert */}
      {data?.summary?.errors > 0 && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-800">Atencao: Problemas Criticos Detectados!</p>
                <p className="text-sm text-red-700 mt-1">
                  {data.summary.errors} componente(s) com erro. Voce recebera um email de alerta automaticamente.
                  Verifique os componentes marcados em vermelho acima.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <div className="text-center text-xs text-muted-foreground">
        <p>O sistema verifica automaticamente a cada 5 minutos.</p>
        <p>Voce recebera um email se algum componente critico falhar.</p>
      </div>
    </div>
  );
}

export default SystemHealthDashboard;
